import classNames from 'classnames';
import styles from 'styles/components/common/elements/Dropdown.module.scss';
import { ReactComponent as ChevronUp } from 'assets/icons/chevron_up.svg';
import { ReactComponent as ChevronDown } from 'assets/icons/chevron_down.svg';
import {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { TSelectObject } from 'types/dropdown';
import { motion, AnimatePresence } from 'framer-motion';

type TProps<TExpandedSelectObject extends TSelectObject> = {
  isSubNav?: boolean;
  placeholder?: string;
  selectList?: TExpandedSelectObject[];
  selectedObject?: TExpandedSelectObject;
  showSelectList: boolean;
  setShowSelectList?: Dispatch<SetStateAction<boolean>>;
  btnStyle: CSSProperties;
  dropdownStyle: CSSProperties;
  onClickButton: () => void;
  onClickSelectItem: (selectObject: TExpandedSelectObject) => void;
  disabled?: boolean;
  readOnly?: boolean;
  defaultValue?: string;
  children?: (arg: TExpandedSelectObject) => ReactNode;
};

const Dropdown = <TExpandedSelectObject extends TSelectObject>({
  isSubNav,
  placeholder,
  selectList,
  selectedObject,
  showSelectList = false,
  setShowSelectList,
  onClickSelectItem,
  btnStyle,
  dropdownStyle,
  onClickButton,
  disabled,
  readOnly,
  defaultValue,
  children,
}: TProps<TExpandedSelectObject>) => {
  const inputRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSelectList && setShowSelectList(() => false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div className={styles.btn_wrapper} ref={inputRef}>
      <button
        type="button"
        className={classNames(styles.select_btn, {
          [styles.readonly]: readOnly,
          [styles.subnav]: isSubNav,
          [styles.active]: selectedObject || defaultValue,
        })}
        onClick={onClickButton}
        style={btnStyle}
        disabled={
          !selectList || selectList.length === 0 || disabled || readOnly
        }
      >
        <span className={styles.span}>
          {selectedObject && selectedObject.label
            ? selectedObject.label
            : defaultValue
            ? defaultValue
            : placeholder}
        </span>
        {!showSelectList ? (
          <ChevronDown
            width={24}
            height={24}
            fill={
              isSubNav && (selectList?.length === 0 || disabled || readOnly)
                ? STYLES_VARIABLES.GRAY_05
                : selectedObject || defaultValue
                ? STYLES_VARIABLES.GRAY_08
                : selectList?.length === 0 || disabled || readOnly
                ? STYLES_VARIABLES.GRAY_04
                : STYLES_VARIABLES.GRAY_07
            }
          />
        ) : (
          <ChevronUp
            width={24}
            height={24}
            fill={
              selectedObject || defaultValue
                ? STYLES_VARIABLES.GRAY_08
                : STYLES_VARIABLES.GRAY_07
            }
          />
        )}
      </button>
      <AnimatePresence>
        {showSelectList && (
          <motion.ul
            className={classNames(styles.option_list, {
              [styles.open]: showSelectList,
            })}
            style={dropdownStyle}
            initial={{ opacity: 0, height: 'auto' }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 'auto' }}
            transition={{ duration: 0.2 }}
          >
            {selectList?.map((item, index) => {
              return (
                <motion.li
                  className={classNames(styles.option_button, {
                    [styles.selected]: item.value === selectedObject?.value,
                  })}
                  onClick={() =>
                    onClickSelectItem ? onClickSelectItem(item) : null
                  }
                  key={`${item.label}_${item.value}_${index}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {children ? (
                    <div className="flex justify_between items_center">
                      {children(item)}
                    </div>
                  ) : (
                    <span>{item.label}</span>
                  )}
                </motion.li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dropdown;
